'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { Button } from '@/components/ui/Button/Button';
import PluginDownloadButton from '@/components/ui/PluginDownloadButton/PluginDownloadButton';
import { analytics } from '@/lib/analytics';
import {
  dismissPluginDownloadModal,
  shouldShowPluginDownloadModal,
} from '@/utils/pluginDownloadModal';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Proceed with the pending tone/model download. */
  onJustDownload: () => void;
}

export function usePluginDownloadIntercept(enabled: boolean) {
  const [open, setOpen] = useState(false);
  const pendingRef = useRef<(() => void) | null>(null);

  const intercept = (action: () => void) => {
    if (!enabled || !shouldShowPluginDownloadModal()) {
      action();
      return;
    }
    pendingRef.current = action;
    setOpen(true);
    analytics.capture('plugin.download_modal.opened');
  };

  const onClose = () => {
    pendingRef.current = null;
    setOpen(false);
  };

  const onJustDownload = () => {
    const next = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    next?.();
  };

  return {
    intercept,
    modal: (
      <PluginDownloadModal open={open} onClose={onClose} onJustDownload={onJustDownload} />
    ),
  };
}

export const PluginDownloadModal = ({ open, onClose, onJustDownload }: Props) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const persist = () => {
    if (dontShowAgain) dismissPluginDownloadModal();
  };

  const handleClose = () => {
    persist();
    onClose();
  };

  const handleJustDownload = () => {
    persist();
    analytics.capture('plugin.download_modal.just_download.clicked');
    onJustDownload();
  };

  const handlePluginDownload = () => {
    persist();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dontShowAgain]);

  useEffect(() => {
    if (open) setDontShowAgain(false);
  }, [open]);

  if (!open) return null;

  return (
    <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
      <div
        ref={modalRef}
        className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
      >
        <div className="flex justify-between items-center p-4">
          <span className="flex items-center gap-4 min-w-0">
            <span className="flex-shrink-0 rounded bg-[#FFFF00] px-2 py-0.5 text-xs text-black">
              NEW
            </span>
            <span className="text-xl font-semibold">Skip the download</span>
          </span>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-white"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-4 pt-0 flex flex-col gap-4">
          <p className="text-zinc-400 text-base">
            The free, official TONE3000 Plugin and standalone app just launched. Browse and load
            tones inside the plugin without downloading, unzipping, and uploading files into a
            third-party plugin.
          </p>
          <p className="text-zinc-400 text-base">
            Learn More:{' '}
            <Link
              href="/plugin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
              onClick={() => analytics.capture('plugin.download_modal.learn_more.clicked')}
            >
              TONE3000 Plugin
            </Link>
          </p>
          <div className="pt-2">
            <Checkbox
              label="Don't show me this again"
              checked={dontShowAgain}
              onChange={setDontShowAgain}
            />
          </div>
          <div className="flex flex-col gap-4">
            <PluginDownloadButton
              variant="primary"
              fullWidth
              surface="plugin_download_modal"
              onDownload={handlePluginDownload}
            />
            <Button isSecondary className="w-full" onClick={handleJustDownload}>
              Just download tone
            </Button>
          </div>
        </div>
      </div>
    </span>
  );
};
